import axios from "axios";

/**
 * Backend URL resolution — same-origin first.
 *
 * Emergent's ingress routes any request to `/api/*` on the current host to the
 * FastAPI backend, so a same-origin call always works regardless of which
 * domain the user loaded (apex vs www, preview vs custom domain).
 *
 * This makes the frontend immune to a whole class of CORS bugs caused by the
 * bundled REACT_APP_BACKEND_URL pointing at a domain that later 308-redirects
 * (as happened with the sterlingbio.co.uk apex→www redirect).
 *
 * Precedence:
 *   1. In a browser → use window.location.origin (always right).
 *   2. In SSR / tests → fall back to REACT_APP_BACKEND_URL from build-time env.
 *   3. If both missing → relative URL (axios will use current origin).
 */
const BACKEND_URL =
  typeof window !== "undefined" && window.location && window.location.origin
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

export const fetchProducts = () => api.get("/products").then((r) => r.data);
export const fetchProduct = (id) => api.get(`/products/${id}`).then((r) => r.data);
export const createOrder = (payload) => api.post("/orders", payload).then((r) => r.data);
export const fetchOrder = (id, token) =>
  api.get(`/orders/${id}`, { params: token ? { token } : {} }).then((r) => r.data);
export const fetchSiteConfig = () => api.get("/site/config").then((r) => r.data);
export const fetchAdminOrders = (archived = false) =>
  api.get("/admin/orders", { params: { archived } }).then((r) => r.data);
export const updateOrderStatus = (orderId, status) =>
  api.patch(`/admin/orders/${orderId}/status`, { status }).then((r) => r.data);
export const updateOrderArchived = (orderId, archived) =>
  api.patch(`/admin/orders/${orderId}/archive`, { archived }).then((r) => r.data);
export const deleteOrder = (orderId) =>
  api.delete(`/admin/orders/${orderId}`).then((r) => r.data);
export const sendPaymentReminders = () =>
  api.post("/admin/orders/send-reminders").then((r) => r.data);
export const fetchAdminDashboard = () =>
  api.get("/admin/orders/dashboard").then((r) => r.data);

// Admin product CRUD
export const adminCreateProduct = (payload) =>
  api.post("/admin/products", payload).then((r) => r.data);
export const adminUpdateProduct = (productId, payload) =>
  api.patch(`/admin/products/${productId}`, payload).then((r) => r.data);
export const adminDeleteProduct = (productId) =>
  api.delete(`/admin/products/${productId}`).then((r) => r.data);
export const adminUploadProductImage = (productId, file, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  return api
    .post(`/admin/products/${productId}/image`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    })
    .then((r) => r.data);
};
export const adminRemoveProductImage = (productId) =>
  api.delete(`/admin/products/${productId}/image`).then((r) => r.data);

// Resolve image_url returned from backend (relative /api/files/...) to absolute URL for <img> tags
export const resolveImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith("http")) return imageUrl;
  return `${BACKEND_URL}${imageUrl}`;
};

export const formatGBP = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);
